import { useEffect, useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { CalendarDays, ChevronLeft, ChevronRight, Lock, Plus } from 'lucide-react'

import { locationsApi, roomBookingsApi, roomsApi, scheduleApi, teachersApi } from '@/api/endpoints'
import type { Lesson, RoomBooking } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { canBrowseTeachers, canManageRoomBookings, isOwner } from '@/auth/permissions'
import { PageHeader } from '@/components/layout/AppLayout'
import { Alert, EmptyState } from '@/components/ui/Alert'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { SkeletonRows } from '@/components/ui/Spinner'
import { cn } from '@/lib/cn'
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  formatDayLong,
  formatDayShort,
  formatMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  toIsoDate,
  weekDays,
} from '@/lib/scheduleTime'
import { LessonDetailModal } from './LessonDetailModal'
import { LessonFormModal } from './LessonFormModal'
import { RoomBookingDetailModal } from './RoomBookingDetailModal'
import { RoomBookingFormModal } from './RoomBookingFormModal'
import { Legenda, MonthView, YearView } from './MonthView'
import { TimeGrid } from './TimeGrid'
import type { GridColumn } from './TimeGrid'

type Widok = 'dzien' | 'tydzien' | 'miesiac' | 'rok'

const WIDOKI: { value: Widok; label: string }[] = [
  { value: 'dzien', label: 'Dzień' },
  { value: 'tydzien', label: 'Tydzień' },
  { value: 'miesiac', label: 'Miesiąc' },
  { value: 'rok', label: 'Rok' },
]

/** Pozycja „wszyscy” w wyborze nauczyciela — pusta wartość znaczy „bez filtru”. */
const WSZYSCY_NAUCZYCIELE = ''
const WSZYSTKIE_SALE = ''

/**
 * Sztuczny identyfikator kolumny z zajęciami dojazdowymi.
 *
 * Nie jest identyfikatorem sali, więc kliknięcie w jej wolną godzinę nie może
 * podstawić sali do formularza — dlatego widok dnia to sprawdza.
 */
const KOLUMNA_DOJAZDU = 'dojazd'

const FILTR_KLASY =
  'cursor-pointer rounded-full border-2 border-transparent bg-surface-container-low px-4 py-2 ' +
  'text-sm font-semibold text-on-surface-variant transition-colors focus:border-primary ' +
  'focus:bg-surface focus:outline-none ' +
  // Telefon w poziomie: pasek narzędzi łamał się na dwa wiersze i zabierał
  // siatce 52 px z 390 dostępnych. Zmierzone — po ściśnięciu mieści się w jednym.
  'telefon-poziomo:px-3 telefon-poziomo:py-1 telefon-poziomo:text-xs'

export function SchedulePage() {
  const { user } = useAuth()

  const [widok, setWidok] = useState<Widok>('tydzien')
  const [anchor, setAnchor] = useState(() => new Date())
  const [locationId, setLocationId] = useState('')
  const [roomId, setRoomId] = useState(WSZYSTKIE_SALE)
  // Administrator prowadzący zajęcia zaczyna od własnego grafiku — po to tu
  // przychodzi. Administracja bez lekcji potrzebuje obrazu całości.
  const [teacherId, setTeacherId] = useState(() =>
    user?.can_teach ? (user?.id ?? WSZYSCY_NAUCZYCIELE) : WSZYSCY_NAUCZYCIELE,
  )
  const [selected, setSelected] = useState<Lesson | null>(null)
  const [editing, setEditing] = useState<Lesson | null>(null)
  // Edycja całego planu tygodniowego korzysta z tego samego formularza,
  // tyle że wypełnia go cykl, a nie pojedyncze zajęcia.
  const [editingSeriesId, setEditingSeriesId] = useState<string | null>(null)
  const [preset, setPreset] = useState<{ day: Date; hour: number; roomId?: string } | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [wybranaRezerwacja, setWybranaRezerwacja] = useState<RoomBooking | null>(null)
  const [rezerwacjaOtwarta, setRezerwacjaOtwarta] = useState(false)

  const locationsQuery = useQuery({
    queryKey: ['locations', 'picker'],
    queryFn: () => locationsApi.list({ size: 200 }),
  })
  const locations = locationsQuery.data?.items ?? []

  // Widok dnia dzieli grafik na sale, więc musi znać oddział. Domyślnie
  // pierwszy dostępny — właściciel może przełączać.
  const effectiveLocationId = locationId || locations[0]?.id || ''

  const zakres = useMemo(() => {
    if (widok === 'dzien') return { from: anchor, to: anchor }
    if (widok === 'tydzien') return { from: startOfWeek(anchor), to: endOfWeek(anchor) }
    if (widok === 'miesiac') {
      return { from: startOfWeek(startOfMonth(anchor)), to: endOfWeek(endOfMonth(anchor)) }
    }
    return {
      from: new Date(anchor.getFullYear(), 0, 1),
      to: new Date(anchor.getFullYear(), 11, 31),
    }
  }, [widok, anchor])

  const useGrid = widok === 'dzien' || widok === 'tydzien'

  // Nauczyciel widzi wyłącznie swój grafik — zawęża go serwer, więc filtr
  // wysyłamy tylko dla administracji, która ma wybór.
  const wybranyNauczyciel = canBrowseTeachers(user) ? teacherId || undefined : undefined
  // Nauczyciel nie ma tego filtru, więc i nie zawężamy mu grafiku do sali.
  const wybranaSala = canBrowseTeachers(user) ? roomId || undefined : undefined

  const lessonsQuery = useQuery({
    queryKey: [
      'schedule',
      'lessons',
      toIsoDate(zakres.from),
      toIsoDate(zakres.to),
      effectiveLocationId,
      wybranyNauczyciel ?? '',
      wybranaSala ?? '',
    ],
    queryFn: () =>
      scheduleApi.lessons({
        date_from: toIsoDate(zakres.from),
        date_to: toIsoDate(zakres.to),
        location_id: effectiveLocationId || undefined,
        teacher_id: wybranyNauczyciel,
        room_id: wybranaSala,
      }),
    enabled: useGrid,
    placeholderData: keepPreviousData,
  })

  // Rezerwacje sal poza zajęciami — w tej samej siatce co lekcje, bo dla
  // planującego to ten sam zajęty pas. Serwer zawęża je do oddziałów konta,
  // więc nauczyciel też je zobaczy.
  const bookingsQuery = useQuery({
    queryKey: [
      'room-bookings',
      toIsoDate(zakres.from),
      toIsoDate(zakres.to),
      effectiveLocationId,
      wybranaSala ?? '',
    ],
    queryFn: () =>
      roomBookingsApi.list({
        date_from: toIsoDate(zakres.from),
        date_to: toIsoDate(zakres.to),
        room_id: wybranaSala,
      }),
    enabled: useGrid,
    placeholderData: keepPreviousData,
  })

  const teachersQuery = useQuery({
    queryKey: ['teachers', 'picker', effectiveLocationId],
    queryFn: () => teachersApi.list({ size: 200, location_id: effectiveLocationId || undefined }),
    enabled: canBrowseTeachers(user),
  })

  // Po zmianie oddziału wybrany nauczyciel może w nim nie uczyć. Bez tego
  // zostawalibyśmy z filtrem, którego nie ma na liście — grafik byłby pusty
  // i nie byłoby widać dlaczego.
  const kadra = teachersQuery.data?.items
  useEffect(() => {
    if (!kadra || teacherId === WSZYSCY_NAUCZYCIELE) return
    if (!kadra.some((item) => item.user_id === teacherId)) setTeacherId(WSZYSCY_NAUCZYCIELE)
  }, [kadra, teacherId])

  const overviewQuery = useQuery({
    queryKey: [
      'schedule',
      'overview',
      toIsoDate(zakres.from),
      toIsoDate(zakres.to),
      effectiveLocationId,
      wybranyNauczyciel ?? '',
      wybranaSala ?? '',
    ],
    queryFn: () =>
      scheduleApi.overview({
        date_from: toIsoDate(zakres.from),
        date_to: toIsoDate(zakres.to),
        location_id: effectiveLocationId || undefined,
        teacher_id: wybranyNauczyciel,
        room_id: wybranaSala,
      }),
    enabled: !useGrid,
    placeholderData: keepPreviousData,
  })

  const roomsQuery = useQuery({
    queryKey: ['rooms', 'picker', effectiveLocationId],
    queryFn: () => roomsApi.list({ size: 200, location_id: effectiveLocationId || undefined }),
    // Sale są potrzebne nie tylko na kolumny widoku dnia, ale i na listę
    // filtru — administracja wybiera salę w każdym widoku.
    enabled: effectiveLocationId !== '' && (widok === 'dzien' || canBrowseTeachers(user)),
  })

  // Po zmianie oddziału wybrana sala stoi w innym budynku. Zostawiona
  // zwracałaby pustą siatkę bez widocznego powodu — tak samo jak filtr kadry.
  const sale = roomsQuery.data?.items
  useEffect(() => {
    if (!sale || roomId === WSZYSTKIE_SALE) return
    if (!sale.some((item) => item.id === roomId)) setRoomId(WSZYSTKIE_SALE)
  }, [sale, roomId])

  const columns: GridColumn[] = useMemo(() => {
    // Wartość domyślna wewnątrz useMemo, nie poza nim — nowa tablica przy
    // każdym renderze unieważniałaby zapamiętany wynik.
    const lessons = lessonsQuery.data ?? []
    const bookings = bookingsQuery.data ?? []

    if (widok === 'dzien') {
      // Zawężenie do sali zostawia jedną kolumnę. Serwer i tak przysłał tylko
      // jej zajęcia, więc pozostałe byłyby pustymi słupkami.
      const widoczneSale = (roomsQuery.data?.items ?? []).filter(
        (room) => wybranaSala === undefined || room.id === wybranaSala,
      )
      const sale = widoczneSale.map((room) => ({
        id: room.id,
        label: room.name,
        sublabel: room.description ?? undefined,
        color: room.color,
        lessons: lessons.filter((lesson) => lesson.room?.id === room.id),
        bookings: bookings.filter((booking) => booking.room.id === room.id),
      }))

      // Zajęcia dojazdowe nie mają sali, więc przepadłyby między kolumnami.
      // Dostają własną — pojawia się tylko w dniach, w których ktoś dojeżdża.
      const dojazdowe = lessons.filter((lesson) => lesson.room === null)
      if (dojazdowe.length === 0) return sale

      return [
        ...sale,
        { id: KOLUMNA_DOJAZDU, label: 'Dojazd', sublabel: 'u ucznia', lessons: dojazdowe },
      ]
    }

    return weekDays(anchor).map((day) => ({
      id: toIsoDate(day),
      label: formatDayShort(day),
      highlighted: isToday(day),
      lessons: lessons.filter((lesson) => toIsoDate(new Date(lesson.starts_at)) === toIsoDate(day)),
      bookings: bookings.filter(
        (booking) => toIsoDate(new Date(booking.starts_at)) === toIsoDate(day),
      ),
    }))
  }, [widok, anchor, lessonsQuery.data, bookingsQuery.data, roomsQuery.data, wybranaSala])

  const przesun = (kierunek: 1 | -1) => {
    if (widok === 'dzien') setAnchor((current) => addDays(current, kierunek))
    else if (widok === 'tydzien') setAnchor((current) => addDays(current, 7 * kierunek))
    else if (widok === 'miesiac') setAnchor((current) => addMonths(current, kierunek))
    else setAnchor((current) => new Date(current.getFullYear() + kierunek, current.getMonth(), 1))
  }

  const tytulZakresu = () => {
    if (widok === 'dzien') return formatDayLong(anchor)
    if (widok === 'tydzien') {
      const monday = startOfWeek(anchor)
      const sunday = endOfWeek(anchor)
      return `${formatDayShort(monday)} – ${formatDayShort(sunday)} ${sunday.getFullYear()}`
    }
    if (widok === 'miesiac') return formatMonth(anchor)
    return String(anchor.getFullYear())
  }

  const openNew = (slot?: { day: Date; hour: number; roomId?: string }) => {
    setEditing(null)
    setPreset(slot ?? { day: anchor, hour: 14 })
    setIsFormOpen(true)
  }

  return (
    <>
      <PageHeader
        title="Grafik lekcji"
        description={tytulZakresu()}
        breadcrumb={['Szkoła', 'Grafik']}
        action={
          <div className="flex flex-wrap items-center gap-2">
            {canManageRoomBookings(user) && (
              <Button
                variant="outline"
                icon={<Lock size={18} />}
                onClick={() => {
                  setPreset(null)
                  setRezerwacjaOtwarta(true)
                }}
              >
                Zarezerwuj salę
              </Button>
            )}
            <Button icon={<Plus size={18} />} onClick={() => openNew()}>
              Nowe zajęcia
            </Button>
          </div>
        }
      />

      <div className="card mb-6 flex flex-wrap items-center gap-3 p-4 telefon-poziomo:mb-3 telefon-poziomo:gap-2 telefon-poziomo:p-2">
        <div className="flex items-center gap-1">
          <IconButton
            label="Poprzedni okres"
            icon={<ChevronLeft size={18} />}
            onClick={() => przesun(-1)}
          />
          <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())}>
            Dzisiaj
          </Button>
          <IconButton
            label="Następny okres"
            icon={<ChevronRight size={18} />}
            onClick={() => przesun(1)}
          />
        </div>

        <div
          className="flex gap-1 rounded-full bg-surface-container-low p-1"
          role="tablist"
          aria-label="Zakres widoku"
        >
          {WIDOKI.map((item) => (
            <button
              key={item.value}
              type="button"
              role="tab"
              aria-selected={widok === item.value}
              onClick={() => setWidok(item.value)}
              className={cn(
                'rounded-full px-4 py-1.5 text-sm font-bold transition-colors',
                'telefon-poziomo:px-3 telefon-poziomo:py-1 telefon-poziomo:text-xs',
                widok === item.value
                  ? 'bg-primary text-on-primary'
                  : 'text-on-surface-variant hover:text-primary',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>

        {(isOwner(user) || locations.length > 1) && (
          <select
            value={effectiveLocationId}
            onChange={(event) => setLocationId(event.target.value)}
            aria-label="Oddział"
            className={FILTR_KLASY}
          >
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        )}

        {/* Kolejność filtrów idzie od najszerszego do najwęższego: oddział →
            sala → nauczyciel. Tak samo szuka się terminu w rozmowie z rodzicem. */}
        {canBrowseTeachers(user) && (
          <select
            value={roomId}
            onChange={(event) => setRoomId(event.target.value)}
            aria-label="Sala"
            className={FILTR_KLASY}
          >
            <option value={WSZYSTKIE_SALE}>Wszystkie sale</option>
            {(roomsQuery.data?.items ?? []).map((room) => (
              <option key={room.id} value={room.id}>
                {room.name}
              </option>
            ))}
          </select>
        )}

        {/* Nauczyciel nie dostaje tego pola: jego grafik to jego zajęcia
            i tak zawęża je serwer. Administracja wybiera, czyj plan ogląda. */}
        {canBrowseTeachers(user) && (
          <select
            value={teacherId}
            onChange={(event) => setTeacherId(event.target.value)}
            aria-label="Nauczyciel"
            className={FILTR_KLASY}
          >
            <option value={WSZYSCY_NAUCZYCIELE}>Wszyscy nauczyciele</option>
            {(teachersQuery.data?.items ?? []).map((item) => (
              <option key={item.user_id} value={item.user_id}>
                {item.user.full_name}
              </option>
            ))}
          </select>
        )}
      </div>

      {(lessonsQuery.isError || overviewQuery.isError) && (
        <Alert tone="error" className="mb-6">
          Nie udało się wczytać grafiku. Odśwież stronę i spróbuj ponownie.
        </Alert>
      )}

      <Card>
        {useGrid ? (
          lessonsQuery.isPending ? (
            <SkeletonRows rows={8} />
          ) : widok === 'dzien' && columns.length === 0 ? (
            <EmptyState
              icon={<CalendarDays size={28} />}
              title="Brak sal w tym oddziale"
              description="Grafik dnia dzieli zajęcia na sale. Dodaj sale w sekcji „Sale”, żeby zacząć planować."
            />
          ) : (
            <>
              <TimeGrid
                columns={columns}
                onSelectLesson={setSelected}
                onSelectBooking={setWybranaRezerwacja}
                onSelectSlot={(columnId, hour) =>
                  openNew(
                    widok === 'dzien'
                      ? {
                          day: anchor,
                          hour,
                          // Kolumna dojazdu nie jest salą — formularz sam
                          // ukryje to pole po wskazaniu ucznia z dojazdem.
                          roomId: columnId === KOLUMNA_DOJAZDU ? undefined : columnId,
                        }
                      : { day: new Date(`${columnId}T00:00:00`), hour },
                  )
                }
                emptyMessage="Brak kolumn do wyświetlenia."
              />
              <div className="px-6 pb-6">
                <Legenda zObecnosciami />
              </div>
            </>
          )
        ) : overviewQuery.isPending ? (
          <SkeletonRows rows={6} />
        ) : widok === 'miesiac' ? (
          <MonthView
            anchor={anchor}
            counts={overviewQuery.data?.days ?? []}
            onSelectDay={(day) => {
              setAnchor(day)
              setWidok('dzien')
            }}
          />
        ) : (
          <YearView
            year={anchor.getFullYear()}
            counts={overviewQuery.data?.months ?? []}
            onSelectMonth={(month) => {
              setAnchor(month)
              setWidok('miesiac')
            }}
          />
        )}
      </Card>

      <LessonDetailModal
        lesson={selected}
        onClose={() => setSelected(null)}
        onEdit={(lesson) => {
          setSelected(null)
          setEditing(lesson)
          setEditingSeriesId(null)
          setPreset(null)
          setIsFormOpen(true)
        }}
        onEditSeries={(seriesId) => {
          setSelected(null)
          setEditing(null)
          setEditingSeriesId(seriesId)
          setPreset(null)
          setIsFormOpen(true)
        }}
      />

      <LessonFormModal
        open={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          setEditing(null)
          setEditingSeriesId(null)
        }}
        locationId={effectiveLocationId}
        preset={preset}
        lesson={editing}
        seriesId={editingSeriesId}
      />

      <RoomBookingFormModal
        open={rezerwacjaOtwarta}
        onClose={() => setRezerwacjaOtwarta(false)}
        preset={preset}
        locationId={effectiveLocationId}
      />

      <RoomBookingDetailModal
        booking={wybranaRezerwacja}
        onClose={() => setWybranaRezerwacja(null)}
      />
    </>
  )
}
